import { useTranslation } from 'react-i18next';
import { BOARD_VIEW_TYPE } from '@/features/tasks/public';
import { ViewTypePopover } from '@/shared/components/custom/view-type-popover';
import type { TemplateView } from '../types';

const ALL_VIEW_TYPES = [
  BOARD_VIEW_TYPE.List,
  BOARD_VIEW_TYPE.Kanban,
  BOARD_VIEW_TYPE.Calendar,
  BOARD_VIEW_TYPE.Gantt,
  BOARD_VIEW_TYPE.Documents,
  BOARD_VIEW_TYPE.Dashboard,
  BOARD_VIEW_TYPE.Activity,
  BOARD_VIEW_TYPE.Timeline,
  BOARD_VIEW_TYPE.Whiteboard,
] as const;

const VIEW_TYPE_KEY: Record<number, string> = {
  [BOARD_VIEW_TYPE.List]: 'list',
  [BOARD_VIEW_TYPE.Kanban]: 'kanban',
  [BOARD_VIEW_TYPE.Calendar]: 'calendar',
  [BOARD_VIEW_TYPE.Gantt]: 'gantt',
  [BOARD_VIEW_TYPE.Documents]: 'documents',
  [BOARD_VIEW_TYPE.Dashboard]: 'dashboard',
  [BOARD_VIEW_TYPE.Activity]: 'activity',
  [BOARD_VIEW_TYPE.Timeline]: 'timeline',
  [BOARD_VIEW_TYPE.Whiteboard]: 'whiteboard',
};

interface TemplateViewPopoverProps {
  existingViews: TemplateView[];
  onAdd: (view: TemplateView) => void;
  isPending?: boolean;
}

/** Adds a view to a tenant template's structure — reuses `ViewTypePopover`, the same shell `boards/AddViewPopover` uses. */
export function TemplateViewPopover({ existingViews, onAdd, isPending }: TemplateViewPopoverProps) {
  const { t } = useTranslation('boards');
  const { t: tTemplates } = useTranslation('projectTemplates');

  const options = ALL_VIEW_TYPES.map((type) => ({
    type,
    label: t(`views.types.${VIEW_TYPE_KEY[type]}`),
  }));

  function handleAdd(type: number) {
    const name = t(`views.types.${VIEW_TYPE_KEY[type]}`);
    onAdd({ type: type as TemplateView['type'], name, isDefault: existingViews.length === 0 });
  }

  return (
    <ViewTypePopover
      options={options}
      existingTypes={existingViews.map((v) => v.type)}
      onAdd={handleAdd}
      isPending={isPending}
      addLabel={tTemplates('preview.addView')}
      testIdPrefix="project-templates-view"
    />
  );
}
